import { getApiClient } from "@src/api";
import { addChatMutation } from "@src/components/board/mutations/add-chat-to-workspace";
import { moveRanklistItemMutation } from "@src/components/board/mutations/move-item";
import {
	optimisticallyRemoveNavbarItem,
	restoreNavbarBranchSnapshots,
} from "@src/components/board/mutations/navbar-optimistic";
import {
	type NavbarBranchItem,
	navbarBranchQueryKeys,
} from "@src/components/board/queries/fetch-ranklist";
import { setFolderHasChildrenInBranchList } from "@src/components/board/queries/navbar-branch-has-children";
import { trashQueryKeys } from "@src/components/board/queries/query-keys";
import { parseChatDocumentId } from "@src/components/collaboration/document-id";
import { libraryQueryKeys } from "@src/components/library/queries/query-keys";
import { graphql } from "@src/gql";
import {
	type ChatMessageInput,
	type ChatPermission,
	NavBarItemType,
	NavbarSection,
	WhiteboardStatus,
} from "@src/gql/graphql";
import { handleError } from "@src/utils/error-handling";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { chatQueryKeys } from "../queries/chat-queries";

export const createChatMutation = graphql(/* GraphQL */ `
	mutation CreateChat($name: String) {
		chats {
			create(name: $name) {
				chatId
				state {
					id
					name
				}
			}
		}
	}
`);

export const archiveChatMutation = graphql(/* GraphQL */ `
	mutation ArchiveChat($chatId: String!) {
		chats {
			archive(chatId: $chatId) {
				chatId
			}
		}
	}
`);

export const renameChatMutation = graphql(/* GraphQL */ `
	mutation RenameChat(
		$chatId: String!
		$name: String!
		$isAutoGenerated: Boolean
	) {
		chats {
			rename(
				chatId: $chatId
				name: $name
				isAutoGenerated: $isAutoGenerated
			) {
				chatId
				state {
					id
					name
				}
			}
		}
	}
`);

export const saveChatMessagesMutation = graphql(/* GraphQL */ `
	mutation SaveChatMessages(
		$chatId: String!
		$messages: [ChatMessageInput!]!
	) {
		chats {
			recordMessages(chatId: $chatId, messages: $messages) {
				chatId
			}
		}
	}
`);

export function useCreateChat() {
	const client = getApiClient();
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async ({
			name,
			parentFolderKey,
		}: {
			name?: string;
			parentFolderKey?: string | null;
		}) => {
			const result = await client.mutate({
				mutation: createChatMutation,
				variables: { name },
			});
			const created = result.data?.chats.create;
			if (created) {
				const targetParentKey = parentFolderKey ?? null;
				const branchKey = navbarBranchQueryKeys.branch(
					NavbarSection.MyWhiteboards,
					targetParentKey,
				);
				const branchItems =
					queryClient.getQueryData<NavbarBranchItem[]>(branchKey);
				const beforeKey = branchItems?.[0]?.key;
				const chatDocKey = parseChatDocumentId(created.chatId).toString();
				try {
					const addRes = await client.mutate({
						mutation: addChatMutation,
						variables: {
							section: NavbarSection.MyWhiteboards,
							chatId: chatDocKey,
							name: created.state?.name || name || "New Chat",
							beforeKey: beforeKey ?? undefined,
							...(targetParentKey ? { parentFolderKey: targetParentKey } : {}),
						},
					});
					let addedNavbarRank = addRes.data?.navbar?.addChat?.rank ?? "";
					if (targetParentKey && !addedNavbarRank) {
						const moveRes = await client.mutate({
							mutation: moveRanklistItemMutation,
							variables: {
								fromSection: NavbarSection.MyWhiteboards,
								toSection: NavbarSection.MyWhiteboards,
								items: [
									{
										itemKey: chatDocKey,
										parentKey: targetParentKey,
										beforeKey: beforeKey ?? null,
									},
								],
								waitForProjections: true,
							},
						});
						addedNavbarRank = moveRes.data?.navbar?.moveItems?.[0]?.rank ?? "";
					}
					return {
						...created,
						navbarRank: addedNavbarRank,
					};
				} catch (err) {
					handleError(
						err instanceof Error ? err : new Error(String(err)),
						"Failed to add chat to workspace navbar",
					);
				}
			}
			return created;
		},
		onSuccess: (data, variables) => {
			if (data?.chatId) {
				const section = NavbarSection.MyWhiteboards;
				const targetParentKey = variables.parentFolderKey ?? null;
				const queryKey = navbarBranchQueryKeys.branch(section, targetParentKey);
				const chatDocId = parseChatDocumentId(data.chatId);
				const chatItem: NavbarBranchItem = {
					key: chatDocId.toString(),
					name: data.state?.name || variables.name || "New Chat",
					type: NavBarItemType.Chat,
					parentKey: targetParentKey,
					rank: "navbarRank" in data ? (data.navbarRank ?? "") : "",
					hasChildren: false,
					status: WhiteboardStatus.Active,
				};
				queryClient.setQueryData<NavbarBranchItem[]>(queryKey, (oldData) => {
					// Do not seed an uninitialized branch with a lone item —
					// that would hide the rest of the workspace until refetch.
					if (!oldData) {
						return oldData;
					}
					if (oldData.some((item) => item.key === chatItem.key)) {
						return oldData;
					}
					return [chatItem, ...oldData];
				});
				if (targetParentKey) {
					const rootQueryKey = navbarBranchQueryKeys.branch(section, null);
					queryClient.setQueryData<NavbarBranchItem[]>(
						rootQueryKey,
						(oldRoot) => oldRoot?.filter((item) => item.key !== chatItem.key),
					);
					setFolderHasChildrenInBranchList(
						queryClient,
						section,
						targetParentKey,
						true,
					);
					void queryClient.invalidateQueries({
						queryKey: rootQueryKey,
					});
				}
			}

			void queryClient.invalidateQueries({
				queryKey: chatQueryKeys.all,
			});
			void queryClient.invalidateQueries({
				queryKey: libraryQueryKeys.entities.all,
			});
			void queryClient.invalidateQueries({
				queryKey: libraryQueryKeys.entityTypeCounts.all,
			});
		},
		onError: (error) => {
			handleError(error, "Failed to create chat");
		},
	});
}

export function useArchiveChat() {
	const client = getApiClient();
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async ({ chatId }: { chatId: string }) => {
			const result = await client.mutate({
				mutation: archiveChatMutation,
				variables: { chatId },
			});
			return result.data?.chats.archive;
		},
		onMutate: async ({ chatId }: { chatId: string }) => {
			const chatDocKey = parseChatDocumentId(chatId).toString();
			const snapshots = await optimisticallyRemoveNavbarItem(
				queryClient,
				chatDocKey,
			);
			return { snapshots };
		},
		onError: (error, _variables, context) => {
			if (context?.snapshots) {
				restoreNavbarBranchSnapshots(queryClient, context.snapshots);
			}
			handleError(error, "Failed to archive chat");
		},
		onSettled: (_data, _error, variables) => {
			void queryClient.invalidateQueries({
				queryKey: navbarBranchQueryKeys.all(),
			});
			void queryClient.invalidateQueries({
				queryKey: chatQueryKeys.all,
			});
			void queryClient.invalidateQueries({
				queryKey: chatQueryKeys.messages(variables.chatId),
			});
			void queryClient.invalidateQueries({
				queryKey: libraryQueryKeys.entities.all,
			});
			void queryClient.invalidateQueries({
				queryKey: libraryQueryKeys.entityTypeCounts.all,
			});
			void queryClient.invalidateQueries({
				queryKey: trashQueryKeys.all(),
			});
		},
	});
}

export function useRenameChat() {
	const client = getApiClient();
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async ({
			chatId,
			name,
			isAutoGenerated,
		}: {
			chatId: string;
			name: string;
			isAutoGenerated?: boolean;
		}) => {
			const result = await client.mutate({
				mutation: renameChatMutation,
				variables: {
					chatId,
					name,
					...(isAutoGenerated !== undefined ? { isAutoGenerated } : {}),
				},
			});
			return result.data?.chats.rename;
		},
		onMutate: async ({ chatId, name }) => {
			await queryClient.cancelQueries({
				queryKey: navbarBranchQueryKeys.all(),
			});
			const chatDocKey = parseChatDocumentId(chatId).toString();
			const previousBranches = queryClient.getQueriesData<NavbarBranchItem[]>({
				queryKey: navbarBranchQueryKeys.all(),
			});
			queryClient.setQueriesData<NavbarBranchItem[]>(
				{ queryKey: navbarBranchQueryKeys.all() },
				(old) => {
					if (!old) return old;
					return old.map((item) =>
						item.key === chatDocKey ? { ...item, name } : item,
					);
				},
			);
			return { previousBranches };
		},
		onError: (error, _variables, context) => {
			if (context?.previousBranches) {
				for (const [queryKey, data] of context.previousBranches) {
					queryClient.setQueryData(queryKey, data);
				}
			}
			handleError(error, "Failed to rename chat");
		},
		onSettled: () => {
			void queryClient.invalidateQueries({
				queryKey: navbarBranchQueryKeys.all(),
			});
			void queryClient.invalidateQueries({
				queryKey: chatQueryKeys.all,
			});
			void queryClient.invalidateQueries({
				queryKey: libraryQueryKeys.entities.all,
			});
			void queryClient.invalidateQueries({
				queryKey: libraryQueryKeys.entityTypeCounts.all,
			});
		},
	});
}

export function useSaveChatMessages() {
	const client = getApiClient();
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async ({
			chatId,
			messages,
		}: {
			chatId: string;
			messages: ChatMessageInput[];
		}) => {
			const result = await client.mutate({
				mutation: saveChatMessagesMutation,
				variables: { chatId, messages },
			});
			return result.data?.chats.recordMessages;
		},
		onSuccess: (_data, variables) => {
			void queryClient.invalidateQueries({
				queryKey: chatQueryKeys.messages(variables.chatId),
			});
			void queryClient.invalidateQueries({
				queryKey: chatQueryKeys.all,
			});
		},
		onError: (error) => {
			handleError(error, "Failed to save chat messages");
		},
	});
}

export const grantChatPermissionMutation = graphql(/* GraphQL */ `
	mutation GrantChatPermission(
		$chatId: String!
		$targetUserId: String!
		$permission: ChatPermission!
	) {
		chats {
			grantPermission(
				chatId: $chatId
				targetUserId: $targetUserId
				permission: $permission
			) {
				chatId
				state {
					id
					shareKey
					permissions {
						key
						value
					}
				}
			}
		}
	}
`);

export const revokeChatPermissionMutation = graphql(/* GraphQL */ `
	mutation RevokeChatPermission($chatId: String!, $targetUserId: String!) {
		chats {
			revokePermission(chatId: $chatId, targetUserId: $targetUserId) {
				chatId
				state {
					id
					shareKey
					permissions {
						key
						value
					}
				}
			}
		}
	}
`);

export const publishChatMutation = graphql(/* GraphQL */ `
	mutation PublishChat(
		$chatId: String!
		$defaultPermission: ChatPermission
	) {
		chats {
			publish(chatId: $chatId, defaultPermission: $defaultPermission) {
				chatId
				state {
					id
					shareKey
				}
			}
		}
	}
`);

export const unpublishChatMutation = graphql(/* GraphQL */ `
	mutation UnpublishChat($chatId: String!) {
		chats {
			unpublish(chatId: $chatId) {
				chatId
				state {
					id
					shareKey
				}
			}
		}
	}
`);

export function useGrantChatPermission() {
	const client = getApiClient();
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async ({
			chatId,
			targetUserId,
			permission,
		}: {
			chatId: string;
			targetUserId: string;
			permission: ChatPermission;
		}) => {
			const result = await client.mutate({
				mutation: grantChatPermissionMutation,
				variables: {
					chatId: parseChatDocumentId(chatId).toString(),
					targetUserId,
					permission,
				},
			});
			return result.data?.chats.grantPermission;
		},
		onSuccess: () => {
			void queryClient.invalidateQueries({
				queryKey: chatQueryKeys.all,
			});
			void queryClient.invalidateQueries({
				queryKey: navbarBranchQueryKeys.all(),
			});
			void queryClient.invalidateQueries({
				queryKey: libraryQueryKeys.entities.all,
			});
			void queryClient.invalidateQueries({
				queryKey: libraryQueryKeys.entityTypeCounts.all,
			});
		},
	});
}

export function useRevokeChatPermission() {
	const client = getApiClient();
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async ({
			chatId,
			targetUserId,
		}: {
			chatId: string;
			targetUserId: string;
		}) => {
			const result = await client.mutate({
				mutation: revokeChatPermissionMutation,
				variables: {
					chatId: parseChatDocumentId(chatId).toString(),
					targetUserId,
				},
			});
			return result.data?.chats.revokePermission;
		},
		onSuccess: () => {
			void queryClient.invalidateQueries({
				queryKey: chatQueryKeys.all,
			});
			void queryClient.invalidateQueries({
				queryKey: navbarBranchQueryKeys.all(),
			});
			void queryClient.invalidateQueries({
				queryKey: libraryQueryKeys.entities.all,
			});
			void queryClient.invalidateQueries({
				queryKey: libraryQueryKeys.entityTypeCounts.all,
			});
		},
	});
}

export function usePublishChat() {
	const client = getApiClient();
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async ({
			chatId,
			defaultPermission,
		}: {
			chatId: string;
			defaultPermission?: ChatPermission;
		}) => {
			const result = await client.mutate({
				mutation: publishChatMutation,
				variables: {
					chatId: parseChatDocumentId(chatId).toString(),
					defaultPermission,
				},
			});
			return result.data?.chats.publish;
		},
		onSuccess: () => {
			void queryClient.invalidateQueries({
				queryKey: chatQueryKeys.all,
			});
			void queryClient.invalidateQueries({
				queryKey: libraryQueryKeys.entities.all,
			});
		},
	});
}

export function useUnpublishChat() {
	const client = getApiClient();
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async (chatId: string) => {
			const result = await client.mutate({
				mutation: unpublishChatMutation,
				variables: {
					chatId: parseChatDocumentId(chatId).toString(),
				},
			});
			return result.data?.chats.unpublish;
		},
		onSuccess: () => {
			void queryClient.invalidateQueries({
				queryKey: chatQueryKeys.all,
			});
			void queryClient.invalidateQueries({
				queryKey: libraryQueryKeys.entities.all,
			});
		},
	});
}
